import { useState } from "react";
import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Loader2, FileSearch, CheckCircle2, CircleDashed, Globe2, Briefcase } from "lucide-react";
import { fetchCase } from "@/lib/api";
import { toFa } from "@/lib/fa";

const TEST_CODES = ["GM7-2026-88", "GM7-2026-99", "GM7-2026-77", "GM7-2026-66"];

export const CaseResult = ({ data }) => (
  <motion.div
    initial={{ opacity: 0, y: 26 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    className="glass-panel mt-6 rounded-3xl border border-gold/25 p-6 sm:p-8"
    data-testid="case-result-panel"
  >
    <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gold/15 pb-5">
      <div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 font-display text-sm font-black text-gold" dir="ltr" data-testid="case-result-code">{data.code}</span>
          <span className="rounded-full bg-emerald-400/10 px-3 py-1.5 text-xs font-bold text-emerald-300">{toFa(data.percent)}٪ پیشرفت</span>
        </div>
        <h3 className="mt-3 font-display text-xl font-bold text-slate-50">{data.clientName}</h3>
      </div>
      <div className="space-y-1.5 text-sm text-slate-400">
        <p className="flex items-center gap-2"><Globe2 size={14} className="text-gold" />{data.country} — {data.visaType}</p>
        <p className="flex items-center gap-2"><Briefcase size={14} className="text-gold" />وکیل مسئول: {data.lawyer}</p>
      </div>
    </div>

    <div className="mt-6 h-2 overflow-hidden rounded-full bg-navy-700">
      <motion.div initial={{ width: 0 }} animate={{ width: `${data.percent}%` }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }} className="h-full rounded-full bg-gradient-to-l from-gold-pale via-gold to-gold-dark" data-testid="case-result-progress-bar" />
    </div>

    <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {data.steps.map((step, i) => {
        const done = i + 1 < data.currentStep;
        const active = i + 1 === data.currentStep;
        return (
          <div key={step} className={`rounded-2xl border p-3 text-center transition-colors ${done ? "border-emerald-400/30 bg-emerald-400/5" : active ? "border-gold/50 bg-gold/10" : "border-slate-700/50 bg-navy-850"}`} data-testid={`case-result-step-${i + 1}`}>
            {done ? <CheckCircle2 size={18} className="mx-auto text-emerald-400" /> : active ? <Loader2 size={18} className="mx-auto animate-spin text-gold" /> : <CircleDashed size={18} className="mx-auto text-slate-600" />}
            <p className={`mt-2 text-xs font-semibold leading-5 ${active ? "text-gold" : done ? "text-emerald-300" : "text-slate-500"}`}>{step}</p>
          </div>
        );
      })}
    </div>

    <div className="mt-8 space-y-3 border-t border-gold/15 pt-6">
      {data.updates.map((u, i) => (
        <div key={i} className="flex flex-wrap items-center gap-3 text-sm" data-testid={`case-update-${i + 1}`}>
          <span className={`h-2 w-2 shrink-0 rounded-full ${u.done ? "bg-emerald-400" : "animate-pulse-dot bg-gold/70"}`} />
          <span className="text-slate-500">{u.date}</span>
          <span className={u.done ? "text-slate-300" : "text-gold/90"}>{u.title}</span>
        </div>
      ))}
    </div>
  </motion.div>
);

export default function TrackingWidget({ full = false }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const latest = useRef(0);

  const search = async (e, codeOverride) => {
    e?.preventDefault?.();
    const c = (codeOverride ?? code).trim();
    if (!c) return;
    const id = ++latest.current;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetchCase(c);
      if (id === latest.current) setResult(res.data);
    } catch {
      if (id === latest.current) setError("کد رهگیری یافت نشد. لطفاً کد را با فرمت صحیح (مثلاً GM7-2026-88) وارد کنید.");
    } finally {
      if (id === latest.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (full && !code && !result) search(null, TEST_CODES[1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [full]);

  return (
    <div>
      <form onSubmit={search} className="flex flex-col gap-3 sm:flex-row" data-testid="tracking-form">
        <div className="relative flex-1">
          <FileSearch size={18} className="absolute right-5 top-1/2 -translate-y-1/2 text-gold/70" />
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="کد رهگیری را وارد کنید؛ مثال: GM7-2026-88"
            dir="ltr"
            className="w-full rounded-full border border-gold/25 bg-navy-850/80 py-4 pl-5 pr-12 text-left text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-500 focus:border-gold/60"
            data-testid="case-tracking-input"
          />
        </div>
        <button type="submit" disabled={loading} className="btn-gold disabled:opacity-60" data-testid="case-tracking-submit-button">
          {loading ? <Loader2 size={17} className="animate-spin" /> : <Search size={17} />}
          {loading ? "در حال جستجو..." : "رهگیری پرونده"}
        </button>
      </form>

      {full && (
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-slate-500">کدهای نمونه برای تست:</span>
          {TEST_CODES.map((c) => (
            <button key={c} onClick={() => search(null, c)} dir="ltr" className="rounded-full border border-gold/25 px-3 py-1.5 font-bold text-gold/90 transition-colors hover:border-gold hover:bg-gold/10" data-testid={`tracking-sample-code-${c}`}>
              {c}
            </button>
          ))}
        </div>
      )}

      {error && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 rounded-2xl border border-red-400/30 bg-red-400/10 p-4 text-sm text-red-300" data-testid="tracking-error-message">{error}</motion.p>
      )}

      <AnimatePresence mode="wait">{result && <CaseResult key={result.code} data={result} />}</AnimatePresence>
    </div>
  );
}
